import { Checkbox, Col, FormInstance, Row } from "antd";
import React, { useEffect } from "react";
import styles from "./CreateUpdateStoreForm.module.scss";
import TimeInput from "../../../commons/components/TimeInput/TimeInput";
import PrimaryButton from "../../../commons/components/PrimaryButton/PrimaryButton";
import dayjs from "dayjs";
import { ValidatorRule } from "rc-field-form/lib/interface";

interface DayScheduleProps {
  form: FormInstance;
  item: {
    openName: string;
    closeName: string;
    title: string;
    breakEndName: string;
    breakStartName: string;
  };
  handleSetDaysSchedule: (index: number) => void;
  handleSetBreakSchedule: (idx: number) => void;
  handleSetBreakScheduleStatus: (idx: number, status: boolean) => void;
  listOpeningHours: boolean[];
  idx: number;
  trigger: boolean;
  setTrigger: React.Dispatch<React.SetStateAction<boolean>>;
  handleDuplicate: (idx: number) => void;
  listDaysHaveOpen: boolean[];
  listBreakingHours: boolean[];
  listDaysHaveBreak: boolean[];
  isInSide: boolean;
}

const DaysSchedule: React.FC<DayScheduleProps> = ({
  setTrigger,
  form,
  item,
  handleSetDaysSchedule,
  idx,
  listOpeningHours,
  handleDuplicate,
  listDaysHaveOpen,
  handleSetBreakSchedule,
  listBreakingHours,
  listDaysHaveBreak,
  isInSide,
  handleSetBreakScheduleStatus,
}) => {
  const [isFocus, setIsFocus] = React.useState<boolean>(false);
  const [isFocusBreak, setIsFocusBreak] = React.useState<boolean>(false);
  const [isError, setIsError] = React.useState<boolean>(false);

  const validateStartBreakTime =
    (name: string): ValidatorRule["validator"] =>
    async (_, value = "") => {
      if (!value) {
        return Promise.resolve();
      }
      if (dayjs(value).isAfter(form.getFieldValue(item.breakEndName))) {
        setIsError(true);
        return Promise.reject(new Error("Start break time must before end break time"));
      }
      if (dayjs(value).isSame(form.getFieldValue(item.openName), "minute")) {
        setIsError(true);
        return Promise.reject(new Error("Start break time must after start open time"));
      }
      if (
        dayjs(value).isBefore(form.getFieldValue(item.openName)) ||
        dayjs(value).isAfter(form.getFieldValue(item.closeName))
      ) {
        setIsError(true);
        return Promise.reject(new Error("Break time must be in between opening and close time"));
      }
      setIsError(false);
      setIsFocusBreak(true);
      return Promise.resolve();
    };

  const validateEndBreakTime =
    (name: string): ValidatorRule["validator"] =>
    async (_, value = "") => {
      if (!value) {
        return Promise.resolve();
      }
      if (dayjs(value).isBefore(form.getFieldValue(item.breakStartName))) {
        setIsError(true);
        return Promise.reject(new Error("End break time must after start break time"));
      }
      if (dayjs(value).isSame(form.getFieldValue(item.closeName), "minute")) {
        setIsError(true);
        return Promise.reject(new Error("End break time must before close time"));
      }
      if (
        dayjs(value).isBefore(form.getFieldValue(item.openName)) ||
        dayjs(value).isAfter(form.getFieldValue(item.closeName))
      ) {
        setIsError(true);
        return Promise.reject(new Error("Break time must be in between opening and close time"));
      }
      setIsError(false);
      setIsFocusBreak(true);
      return Promise.resolve();
    };

  useEffect(() => {
    if (!listOpeningHours[idx]) {
      // reset error for open time
      form.setFields([
        {
          name: item.openName,
          errors: [],
          value: null,
        },
      ]);
      // reset error for close time
      form.setFields([
        {
          name: item.closeName,
          errors: [],
          value: null,
        },
      ]);
    }
  }, [listOpeningHours]);

  useEffect(() => {
    if (!isInSide) {
      setIsFocus(false);
      setIsFocusBreak(false);
    }
  }, [isInSide, isFocus, isFocusBreak]);

  return (
    <div
      key={item.title}
      // onFocus={e => {
      //   setIsFocus(true);
      // }}
    >
      <>
        <Row>
          <Col xs={24} sm={3}></Col>
          <Col xs={24} sm={3}>
            <p className={styles.titleOpenHour}>Opening time</p>
          </Col>
          <Col xs={24} sm={3}>
            <p className={styles.titleOpenHour}>Closing time</p>
          </Col>
          <Col xs={24} sm={3}>
            <p className={styles.titleOpenHour}>Day off</p>
          </Col>
          <Col xs={24} sm={3}>
            <p className={styles.titleOpenHour}>Start shift break</p>
          </Col>
          <Col xs={24} sm={3}>
            <p className={styles.titleOpenHour}>End shift break</p>
          </Col>
          <Col xs={24} sm={3}>
            <p className={styles.titleOpenHour}>Shift break</p>
          </Col>
        </Row>

        <div>
          <Row gutter={10}>
            <div
              style={{ display: "contents" }}
              onFocus={e => {
                setIsFocus(true);
                setIsFocusBreak(false);
              }}
            >
              <Col xs={24} sm={3}>
                <span className={styles.titleOpenHour}>{item.title}</span>
              </Col>
              <Col xs={24} sm={3}>
                <TimeInput
                  label={""}
                  name={item.openName}
                  format={"HH:mm"}
                  placeholder="Select start time"
                  rules={[
                    {
                      required: !listOpeningHours[idx] ? false : true,
                      message: "Please input open time",
                    },
                    {
                      validator: (_, value) => {
                        if (dayjs(value).isAfter(form.getFieldValue(item.closeName))) {
                          setIsError(true);
                          return Promise.reject(new Error("Opening time can't be later than closing time"));
                        }
                        setIsError(false);
                        setIsFocus(true);
                        return Promise.resolve();
                      },
                    },
                  ]}
                  onChange={() => {
                    form.validateFields([item.closeName, item.openName]);
                    setTrigger(prev => !prev);
                  }}
                  disabled={!listOpeningHours[idx]}
                />
              </Col>
              <Col xs={24} sm={3}>
                <TimeInput
                  placeholder="Select end time"
                  name={item.closeName}
                  label=""
                  format={"HH:mm"}
                  rules={[
                    {
                      required: !listOpeningHours[idx] ? false : true,
                      message: "Please input close time",
                    },
                    {
                      validator: (_, value) => {
                        if (dayjs(value).isBefore(form.getFieldValue(item.openName))) {
                          setIsError(true);
                          return Promise.reject(new Error("Closing time can't be earlier than opening time"));
                        }
                        setIsError(false);
                        setIsFocus(true);
                        return Promise.resolve();
                      },
                    },
                  ]}
                  onChange={() => {
                    form.validateFields([item.closeName, item.openName]);
                    setTrigger(prev => !prev);
                  }}
                  disabled={!listOpeningHours[idx]}
                />
              </Col>
              <Col xs={24} sm={3}>
                <div>
                  <Checkbox
                    className={styles.checkBox}
                    checked={!listOpeningHours[idx]}
                    onChange={e => {
                      form.resetFields([item.openName, item.closeName, item.breakEndName, item.breakStartName]);
                      form.setFieldsValue({
                        [item.openName]: undefined,
                        [item.closeName]: undefined,
                        [item.breakEndName]: undefined,
                        [item.breakStartName]: undefined,
                      });
                      handleSetDaysSchedule(idx);

                      if (e.target.checked) {
                        handleSetBreakScheduleStatus(idx, true);
                      }

                      setIsFocus(false);
                    }}
                  />
                </div>
              </Col>
            </div>
            {/* break time */}

            <div
              style={{ display: "contents" }}
              onFocus={e => {
                setIsFocus(false);
                setIsFocusBreak(true);
              }}
            >
              <Col xs={24} sm={3}>
                <TimeInput
                  label={""}
                  placeholder="Select start time"
                  name={item.breakStartName}
                  format={"HH:mm"}
                  rules={[
                    {
                      required: listBreakingHours[idx] ? false : true,
                      message: "Please input break time",
                    },
                    {
                      validator: validateStartBreakTime(item.breakStartName),
                    },
                  ]}
                  onChange={() => {
                    setTrigger(prev => !prev);
                    form.validateFields([item.breakStartName, item.breakEndName]);
                  }}
                  disabled={listBreakingHours[idx]}
                />
              </Col>
              <Col xs={24} sm={3}>
                <TimeInput
                  name={item.breakEndName}
                  placeholder="Select end time"
                  label=""
                  format={"HH:mm"}
                  rules={[
                    {
                      required: listBreakingHours[idx] ? false : true,
                      message: "Please input break time",
                    },
                    {
                      validator: validateEndBreakTime(item.breakEndName),
                    },
                  ]}
                  onChange={() => {
                    form.validateFields([item.breakStartName, item.breakEndName]);
                    setTrigger(prev => !prev);
                  }}
                  disabled={listBreakingHours[idx]}
                />
              </Col>
              <Col xs={24} sm={3}>
                <div>
                  <Checkbox
                    className={styles.checkBox}
                    checked={!listBreakingHours[idx]}
                    onChange={e => {
                      form.resetFields([item.breakEndName, item.breakStartName]);
                      form.setFieldsValue({ [item.breakStartName]: undefined, [item.breakEndName]: undefined });
                      handleSetBreakSchedule(idx);
                      setIsFocusBreak(false);
                    }}
                  />
                </div>
              </Col>
            </div>
            <div>
              {listDaysHaveOpen[idx] &&
                isFocus &&
                !isFocusBreak &&
                form.getFieldsValue([item.openName])[item.openName] &&
                form.getFieldsValue([item.closeName])[item.closeName] &&
                !isError &&
                isInSide && (
                  <PrimaryButton
                    type="button"
                    onClick={() => {
                      handleDuplicate(idx);
                      setIsFocus(false);
                    }}
                  >
                    Duplicate
                  </PrimaryButton>
                )}
              {listDaysHaveBreak[idx] &&
                isFocusBreak &&
                !isFocus &&
                form.getFieldsValue([item.breakStartName])[item.breakStartName] &&
                form.getFieldsValue([item.breakEndName])[item.breakEndName] &&
                !isError &&
                isInSide && (
                  <PrimaryButton
                    type="button"
                    onClick={() => {
                      handleDuplicate(idx);
                      setIsFocusBreak(false);
                    }}
                  >
                    Duplicate
                  </PrimaryButton>
                )}
            </div>
          </Row>
        </div>
      </>
    </div>
  );
};

export default DaysSchedule;
